import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoute } from '@app/core/enums/app-route.enum';
import { FundFamilyService } from '@app/modules/companies/services/fund-family.service';
import { PortfolioReviewService } from '@app/modules/portfolio/services/portfolio-review.service';
import { CompanyStage } from '@common/enums/CompanyStage';
import { InvestmentActivity } from '@common/enums/InvestmentActivity';
import { QuantumFunds } from '@common/enums/tearsheet/TearsheetState';
import { IAdminEventTask } from '@common/models/IAdminEventTask';
import { IAlertEventConfig } from '@common/models/IAlertEvent';
import { Utils } from '@common/utils/utils';
import { map, Subscription } from 'rxjs';
import { Table } from 'primeng/table';
import { AlertEvent } from '../alerts-workflow/workflow-timeline/workflow-timeline.component';

@Component({
  selector: 'app-view-tasks',
  templateUrl: './view-tasks.component.html',
  styleUrls: ['./view-tasks.component.scss']
})
export class ViewTasksComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() event: AlertEvent | undefined = undefined;
  @Input() isShowFilter: boolean = true;
  @Output() visibleChange = new EventEmitter<boolean>();

  @ViewChild('taskTable') taskTable!: Table;

  // Local copy drives the dialog's [(visible)] so PrimeNG's close button works correctly.
  // The parent's [visible] input syncs into this via ngOnChanges.
  _isVisible = false;

  tasks: IAdminEventTask[] = [];
  visibleTasks: IAdminEventTask[] = [];
  showExitedTasks = false;
  showActivityTask = false;
  showValuationTask = false;
  showWriteUpTask = false;
  showTearsheetTask = false;
  showBoardBookApprovalTask = false;
  showFinancialApprovalTask = false;
  showStatusTask = false;
  readonly companyStage = CompanyStage;

  private allCompanies: any[] = [];
  private funds: any[] = [];
  private existingMnemonics = new Set<string>();
  private subscription = new Subscription();

  constructor(
    private readonly router: Router,
    private readonly fundFamilyService: FundFamilyService,
    private readonly portfolioReviewService: PortfolioReviewService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.fundFamilyService.getAll().subscribe(res => {
        this.funds = res.map(f => f.name);
      })
    );

    this.subscription.add(
      this.portfolioReviewService.getAll().pipe(
        map(pcs => pcs.filter(c =>
          c.fundFamily &&
          !c.fundFamily.includes(QuantumFunds.QIFIFundFamily) &&
          !c.fundFamily.includes(QuantumFunds.QIFIIFundFamily)
        ))
      ).subscribe(companies => {
        this.allCompanies = companies;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      this._isVisible = this.visible;
      if (this.visible && this.event) {
        this.initTasks();
      }
      if (!this.visible) {
        this.showExitedTasks = false;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getDefaultConfig(eventType: string): IAlertEventConfig {
    const base: IAlertEventConfig = {
      showActivityTask: false, showValuationTask: false, showWriteUpTask: false,
      showTearsheetTask: false, showBoardBookApprovalTask: false,
      showFinancialApprovalTask: false, showStatusTask: false,
    };
    switch (eventType) {
      case InvestmentActivity.Valuation:
        return { ...base, showActivityTask: true, showValuationTask: true, showWriteUpTask: true };
      case InvestmentActivity.Tearsheets:
        return { ...base, showTearsheetTask: true };
      case InvestmentActivity.Documents:
        return { ...base, showBoardBookApprovalTask: true, showFinancialApprovalTask: true };
      case InvestmentActivity.Review:
        return { ...base, showActivityTask: true, showStatusTask: true };
      default:
        return { ...base, showStatusTask: true };
    }
  }

  private initTasks(): void {
    this.showExitedTasks = false;

    const config = this.event!.taskConfig || this.getDefaultConfig(this.event!.eventType as string);
    this.showActivityTask = config.showActivityTask;
    this.showValuationTask = config.showValuationTask;
    this.showWriteUpTask = config.showWriteUpTask;
    this.showTearsheetTask = config.showTearsheetTask;
    this.showBoardBookApprovalTask = config.showBoardBookApprovalTask;
    this.showFinancialApprovalTask = config.showFinancialApprovalTask;
    this.showStatusTask = config.showStatusTask;

    const existingTasks = this.event!.tasks || [];
    this.existingMnemonics = new Set(existingTasks.map(t => t.mnemonic));

    let otherTasks = this.allCompanies
      .filter(c => !this.existingMnemonics.has(c.mnemonic))
      .map(c => ({
        isCreated: false,
        approvalStage: 'N/A',
        valuationApprovalStage: 'N/A',
        valuationWriteupStage: 'N/A',
        financialApprovalStage: 'N/A',
        boardBookApprovalStage: 'N/A',
        tearsheetApprovalStage: 'N/A',
        valuationUpdatedBy: 'N/A',
        tearsheetUpdatedBy: 'N/A',
        boardMeetingUpdatedAt: null as unknown as Date,
        financialStatementUpdatedAt: null as unknown as Date,
        mnemonic: c.mnemonic,
        company: c.name || c.mnemonic,
        fundFamily: c.fundFamily,
        activity: 'N/A',
        completionDate: null as unknown as Date,
        stage: c.stage
      } as IAdminEventTask));

    if (this.showTearsheetTask) {
      otherTasks = otherTasks.filter(task => {
        const fundFamily = task.fundFamily || [];
        return !(
          fundFamily.includes(QuantumFunds.QIFIFundFamily) ||
          fundFamily.includes(QuantumFunds.QIFIIFundFamily)
        );
      });
    }

    this.tasks = [...existingTasks, ...otherTasks];

    if (this.taskTable) {
      this.taskTable.clear();
    }

    const fundOrderMap = new Map(this.funds.map((f, index) => [f, index]));
    const getFundIndex = (task: IAdminEventTask): number => {
      if (!task.fundFamily || task.fundFamily.length === 0) return Number.MAX_SAFE_INTEGER;
      for (const f of task.fundFamily) {
        if (fundOrderMap.has(f)) return fundOrderMap.get(f)!;
      }
      return Number.MAX_SAFE_INTEGER;
    };

    this.tasks.sort((a, b) => {
      const fundDiff = getFundIndex(a) - getFundIndex(b);
      if (fundDiff !== 0) return fundDiff;
      return (a.company || '').toLowerCase().localeCompare((b.company || '').toLowerCase());
    });

    this.updateVisibleTasks(this.showExitedTasks);
  }

  updateVisibleTasks(show: boolean): void {
    this.showExitedTasks = show;

    const existingActive: IAdminEventTask[] = [];
    const nonExistingActive: IAdminEventTask[] = [];
    const forming: IAdminEventTask[] = [];
    const windDown: IAdminEventTask[] = [];
    const exited: IAdminEventTask[] = [];
    for (const t of this.tasks) {
      if (t.stage === CompanyStage.Exited) exited.push(t);
      else if (t.stage === CompanyStage.WindDown) windDown.push(t);
      else if (t.stage === CompanyStage.Forming) forming.push(t);
      else if (this.existingMnemonics.has(t.mnemonic)) existingActive.push(t);
      else nonExistingActive.push(t);
    }

    if (!show) {
      this.visibleTasks = [...existingActive];
      return;
    }

    this.visibleTasks = [...existingActive, ...nonExistingActive, ...forming, ...windDown, ...exited];
  }

  redirectToTask(task: IAdminEventTask): void {
    if (!task?.mnemonic) return;
    const subtab = this.event!.eventType == InvestmentActivity.Others ? this.event?.association?.toLowerCase() : this.event?.eventType?.toLowerCase();
    if (subtab) {
      this.router.navigate([AppRoute.Companies, task.mnemonic, subtab], { queryParams: { name: this.event!.quarter } });
    } else {
      this.router.navigate([AppRoute.Companies, task.mnemonic, AppRoute.Overview]);
    }
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  get taskDialogCompleteCount(): number {
    return this.visibleTasks.filter(task => task.isCreated).length;
  }

  private readonly stageTooltipMap: Partial<Record<CompanyStage, string>> = {
    [CompanyStage.Exited]: 'Exited PC',
    [CompanyStage.Forming]: 'Forming PC',
    [CompanyStage.WindDown]: 'Wind Down PC',
  };

  getRowTooltip(task: any): string {
    return this.stageTooltipMap[task.stage as CompanyStage]
      ?? (task.approvalStage === 'carried-forward-file' ? 'Data is outdated' : '');
  }

  isNonActiveStage(stage: string | undefined, mnemonic?: string): boolean {
    return stage === CompanyStage.Exited
      || stage === CompanyStage.Forming
      || stage === CompanyStage.WindDown
      || (mnemonic !== undefined && !this.existingMnemonics.has(mnemonic));
  }

  get totalVisibleTasksCount(): number {
    return this.visibleTasks.filter(task => !this.isNonActiveStage(task.stage, task.mnemonic)).length;
  }

  customColumnSort(event: any): void {
    const { data, field, order } = event;
    const rowOrder = (task: any): number => {
      switch (task.stage) {
        case CompanyStage.Exited:   return 4;
        case CompanyStage.WindDown: return 3;
        case CompanyStage.Forming:  return 2;
        default: return this.existingMnemonics.has(task.mnemonic) ? 0 : 1;
      }
    };

    data.sort((a: any, b: any) => {
      const rowDiff = rowOrder(a) - rowOrder(b);
      if (rowDiff !== 0) return rowDiff;
      return order * Utils.compareColumnValues(a[field], b[field]);
    });
  }
}
